import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "components/ui/BootstrapCompat";
import useLocalStorage from "helpers/useLocalStorage";
import {
  donateErrorMessage,
  donateDashboardGetSummary,
  donationGetScreenHistory,
  donationShowOnScreen,
} from "services/main/donate.service";
import { getUserProfileData, userGetMe } from "services/user.service";
import { FiHeart } from "react-icons/fi";
import { RiPushpinFill } from "react-icons/ri";
import { errorAdd, successAdd } from "helpers/sweetalert";

type DonationItem = {
  _id?: string;
  id?: string;
  donationId?: string;
  ref?: string;
  donorName?: string;
  name?: string;
  donor?: string;
  message?: string;
  currency?: string;
  amount?: number | string;
  status?: string;
  createdAt?: string;
  displayCount?: number | string;
  lastDisplayedAt?: string;
  screenDisplayCount?: number | string;
  showCount?: number | string;
  paymentMethod?: string;
  paymentChannel?: string;
  payment?: string | { name?: string; type?: string };
};

const PAGE_SIZE = 20;

const statusLabel: Record<string, string> = {
  SUCCESS: "ສຳເລັດ",
  IN_QUEUE: "ໃນຄິວ",
  CREATED: "ສ້າງແລ້ວ",
  FAILED: "ລົ້ມເຫລວ",
};

const formatCurrency = (amount: number | string) => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat("lo-LA").format(
    Number.isFinite(value) ? value : 0,
  );
};

const formatDateTime = (value?: string) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleString("lo-LA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value?: string) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleTimeString("lo-LA", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeDonationList = (response: any): DonationItem[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data?.donations)) return response.data.donations;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.list)) return response.list;
  if (Array.isArray(response?.donations)) return response.donations;
  return [];
};

const getDonationTotalCount = (response: any) => {
  const candidates = [
    response?.count,
    response?.total,
    response?.totalCount,
    response?.data?.count,
    response?.data?.total,
    response?.data?.totalCount,
    response?.data?.data?.count,
    response?.data?.data?.total,
    response?.data?.data?.totalCount,
  ];
  const value = candidates.find(
    (candidate) =>
      candidate !== undefined && candidate !== null && candidate !== "",
  );
  const count = Number(value);

  return Number.isFinite(count) ? count : undefined;
};

const getDonationId = (donation: DonationItem) =>
  donation?.donationId || donation?._id || donation?.id || "";

const getDonationIdPreview = (donationId: string) =>
  donationId ? donationId.slice(-5) : "--";

const getDonorName = (donation: DonationItem) =>
  donation?.donorName || donation?.name || donation?.donor || "--";

const getDisplayCount = (donation: DonationItem) => {
  const value =
    donation?.displayCount ??
    donation?.screenDisplayCount ??
    donation?.showCount ??
    0;
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const getPaymentMethodLabel = (donation: DonationItem) => {
  if (typeof donation.payment === "string") return donation.payment;
  return (
    donation.paymentMethod ||
    donation.paymentChannel ||
    donation.payment?.name ||
    donation.payment?.type ||
    "--"
  );
};

const toFiniteNumber = (value: unknown) => {
  const normalized =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
};

const getFirstNumber = (source: any, keys: string[]) => {
  for (const key of keys) {
    const value = toFiniteNumber(source?.[key]);
    if (value) return value;
  }
  return 0;
};

const getFirstString = (source: any, keys: string[]) => {
  for (const key of keys) {
    const value = String(source?.[key] || "").trim();
    if (value) return value;
  }
  return "";
};

const getDonateUrl = (user: any) => {
  const backendUrl = getFirstString(user, [
    "donationWebsiteUrl",
    "donateWebsiteUrl",
    "publicDonationUrl",
    "publicDonateUrl",
    "donationUrl",
    "donateUrl",
  ]);
  if (backendUrl) return backendUrl;

  const username = String(user?.username || "").trim();
  return username
    ? `https://donate.onetop.la/${encodeURIComponent(username)}`
    : "";
};

function DonationDetailField({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-[#edf1f6] bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
      <div className="text-[11px] font-black uppercase text-[#667085] dark:text-slate-400">
        {label}
      </div>
      <div
        className={`mt-1 min-w-0 break-words text-[13px] font-black text-[#101828] dark:text-slate-100 ${
          mono ? "font-mono" : ""
        }`}
      >
        {value || "--"}
      </div>
    </div>
  );
}

export default function DonationsPage() {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [showingDonationId, setShowingDonationId] = useState("");
  const [selectedDonation, setSelectedDonation] = useState<DonationItem | null>(
    null,
  );
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  const {
    data: donationPages,
    isLoading,
    isRefetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ["donate/screen-history", businessId],
    async ({ pageParam = 0 }) => {
      const skip = Math.max(Number(pageParam) || 0, 0);
      if (!token) {
        return {
          data: [],
          count: 0,
          skip,
          limit: PAGE_SIZE,
        };
      }

      const response = await donationGetScreenHistory({
        token,
        businessId,
        skip,
        limit: PAGE_SIZE,
      });

      return response;
    },
    {
      enabled: !!token,
      getNextPageParam: (lastPage, allPages) => {
        const rows = normalizeDonationList(lastPage);
        const loadedCount = allPages.reduce(
          (total, page) => total + normalizeDonationList(page).length,
          0,
        );
        const totalCount = getDonationTotalCount(lastPage);

        if (typeof totalCount === "number" && loadedCount >= totalCount) {
          return undefined;
        }

        if (rows.length < PAGE_SIZE) return undefined;

        return loadedCount;
      },
    },
  );

  const { data: summaryResponse } = useQuery(
    ["donations-summary", businessId],
    () => donateDashboardGetSummary({ token, businessId, status: "SUCCESS" }),
    {
      enabled: !!token,
      retry: false,
    },
  );

  const { data: userResponse } = useQuery(
    ["donations-user-me", token, businessId],
    () => userGetMe({ token, businessId }),
    {
      enabled: !!token,
      retry: false,
    },
  );

  const donationList = React.useMemo(
    () =>
      (donationPages?.pages || []).flatMap((page) =>
        normalizeDonationList(page),
      ),
    [donationPages],
  );
  const donationIds = React.useMemo(
    () =>
      donationList.map((donation) => getDonationId(donation)).filter(Boolean),
    [donationList],
  );
  const isInitialListLoading =
    (isLoading || isRefetching) && !donationList.length;
  const summaryPayload = summaryResponse as any;
  const summaryData =
    summaryPayload?.data?.data || summaryPayload?.data || summaryPayload || {};
  const currentUser = getUserProfileData(userResponse);
  const donationListAmount = donationList.reduce(
    (total, donation) => total + toFiniteNumber(donation?.amount),
    0,
  );
  const donationRevenueAmount =
    getFirstNumber(summaryData, [
      "donateAmount",
      "donationAmount",
      "donateTotal",
      "donationTotal",
      "totalDonationAmount",
      "totalAmount",
      "revenue",
      "amount",
    ]) || donationListAmount;
  const donationWebsiteUrl = getDonateUrl(currentUser);
  const donationWebsiteDescription =
    getFirstString(currentUser, [
      "donationWebsiteDescription",
      "donateWebsiteDescription",
      "websiteDescription",
      "bio",
      "description",
    ]) || "ໃຊ້ link ນີ້ເພື່ອແຊຣ໌ໜ້າເວັບ Donate ຂອງທ່ານ";

  React.useEffect(() => {
    const element = loadMoreRef.current;
    if (!element || !hasNextPage || isFetchingNextPage || isLoading) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: "220px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [
    donationList.length,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  ]);

  const showOnScreenMutation = useMutation(
    async (donationId: string) => {
      if (!donationId) throw new Error("Donation ID is required");
      return donationShowOnScreen({
        id: donationId,
        token,
        businessId,
      });
    },
    {
      onMutate: (donationId) => {
        setShowingDonationId(donationId);
      },
      onSuccess: () => {
        successAdd("ສົ່ງຂື້ນຈໍສຳເລັດ");
        queryClient.invalidateQueries(["donate/screen-history", businessId]);
      },
      onError: (error: any) => {
        errorAdd(donateErrorMessage(error));
      },
      onSettled: () => {
        setShowingDonationId("");
      },
    },
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAccounts(donationIds);
    } else {
      setSelectedAccounts([]);
    }
  };

  const handleSelectAccount = (accountId: string, checked: boolean) => {
    if (checked) {
      setSelectedAccounts([...selectedAccounts, accountId]);
    } else {
      setSelectedAccounts(selectedAccounts.filter((id) => id !== accountId));
    }
  };

  const handleCopyDonationId = async (donationId: string) => {
    if (!donationId) {
      errorAdd("ບໍ່ມີ ID ສຳລັບສຳເນົາ");
      return;
    }

    try {
      await navigator.clipboard.writeText(donationId);
      successAdd("ສຳເນົາ ID ສຳເລັດ");
    } catch {
      errorAdd("ບໍ່ສາມາດສຳເນົາ ID ໄດ້");
    }
  };

  const handleCopyText = async (text: string, successMessage: string) => {
    if (!text) {
      errorAdd("ບໍ່ພົບ link ສຳລັບສຳເນົາ");
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      successAdd(successMessage);
    } catch {
      errorAdd("ບໍ່ສາມາດສຳເນົາ link ໄດ້");
    }
  };

  const selectedDonationId = selectedDonation
    ? getDonationId(selectedDonation)
    : "";
  const isSelectedDonationShowing =
    showOnScreenMutation.isLoading && showingDonationId === selectedDonationId;

  return (
    <main className="min-h-full min-w-0">
      <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        <Card>
          <Card.Header>ວິທີຮັບການສະໜັບສະໜຸນ</Card.Header>
          <Card.Body>
            <div className="rounded border border-[#dbe3ec] bg-white p-3 text-[12px] text-[#475467] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              <div className="mb-2 font-bold text-[#101828] dark:text-slate-100">
                ວິທີແຊຣ໌ໜ້າ Donate
              </div>
              <div className="space-y-1.5">
                <div>
                  1. Copy link "
                  {donationWebsiteUrl ? (
                    <button
                      type="button"
                      onClick={() =>
                        handleCopyText(
                          donationWebsiteUrl,
                          "ສຳເນົາ link Donate ສຳເລັດ",
                        )
                      }
                      className="max-w-full break-all text-left text-[12px] font-bold text-primary-500 underline"
                    >
                      {donationWebsiteUrl}
                    </button>
                  ) : (
                    <span className="font-bold text-[#101828] dark:text-slate-100">
                      ລໍຖ້າ backend ສົ່ງ link ເວັບ
                    </span>
                  )}
                  " ແລ້ວແຊຣ໌ໃຫ້ຜູ້ສົນັບສະໜຸນ.
                </div>
                <div>2. ໃຫ້ຜູ້ສົນັບສະໜຸນເຂົ້າ link ແລະເລືອກຈຳນວນເງິນ.</div>
                <div>3. ເມື່ອຊຳລະສຳເລັດ ລາຍການຈະສະແດງໃນໜ້ານີ້.</div>
              </div>
              <div className="mt-2 text-[11px] text-gray-500">
                {donationWebsiteDescription}
              </div>
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
              ລາຍການ Donate ທີ່ໂຫລດແລ້ວ: {donationList.length} ລາຍການ
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>ລາຍໄດ້ຈາກເວັບ Donate</Card.Header>
          <Card.Body>
            <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
              {formatCurrency(donationRevenueAmount)}
              <span className="text-[14px]">ກີບ</span>
            </h1>
            <div className="text-[12px] text-gray-600">
              ລາຍໄດ້ລວມຈາກລາຍການ Donate ທີ່ backend ສົ່ງກັບມາ
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ເງື່ອນໄຂ Donate</Card.Header>
          <Card.Body>
            <div className="rounded border border-[#dbe3ec] bg-white p-3 text-[12px] text-[#475467] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              ເງື່ອນໄຂການ Donate, ຄ່າທຳນຽມ, ການຂື້ນຈໍ ແລະການຖອນເງິນ ຄວນອີງຕາມ
              policy ຈາກ backend.
            </div>
            <Link
              to="/donations/conditions"
              className="mt-2 inline-flex text-[12px] font-bold text-primary-500 underline"
            >
              ເບິ່ງເງື່ອນໄຂ
            </Link>
          </Card.Body>
        </Card>
      </div>

      <div>
        <Card className="donations-mobile-unframed">
          <Card.Header className="hidden md:block">ລາຍການ Donate</Card.Header>
          <Card.Body className="p-0">
            <div className="block md:hidden">
              {isInitialListLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }, (_, index) => (
                    <div
                      key={`donation-mobile-skeleton-${index}`}
                      className="flex items-center gap-3 rounded-lg border border-[#dbe4ef] bg-white px-3 py-2.5 shadow-[0_8px_20px_rgba(16,24,40,0.05)] dark:border-slate-700 dark:bg-slate-900"
                    >
                      <div className="h-10 w-10 flex-none animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
                      <div className="h-3 w-16 flex-1 animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
                      <div className="h-4 w-24 flex-none animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
                    </div>
                  ))}
                </div>
              ) : donationList.length > 0 ? (
                <div className="space-y-2">
                  {donationList.map((donation, index) => {
                    const donationId = getDonationId(donation);

                    return (
                      <button
                        type="button"
                        key={donationId || index}
                        className="flex w-full min-w-0 items-center gap-3 rounded-lg border border-[#dbe4ef] bg-white px-3 py-2.5 text-left shadow-[0_8px_20px_rgba(16,24,40,0.05)] transition hover:border-primary hover:bg-[#f8fbff] focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                        onClick={() => setSelectedDonation(donation)}
                        aria-label="Open donation detail"
                      >
                        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
                          <FiHeart className="h-5 w-5" />
                        </span>

                        <div className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-black text-[#344054] dark:text-slate-100">
                            {formatTime(donation?.createdAt)}
                          </span>
                        </div>

                        <span
                          className="max-w-[46%] flex-none truncate text-right text-[15px] font-black leading-tight text-emerald-600"
                          title={`${formatCurrency(donation?.amount || 0)} ${
                            donation?.currency || "ກີບ"
                          }`}
                        >
                          +{formatCurrency(donation?.amount || 0)}
                        </span>
                      </button>
                    );
                  })}

                  <div
                    ref={loadMoreRef}
                    className="flex min-h-[56px] items-center justify-center text-center text-xs font-medium text-slate-500"
                  >
                    {isFetchingNextPage ? (
                      <span className="inline-flex items-center gap-2">
                        <Spinner animation="border" size="sm" />
                        ກຳລັງໂຫລດເພີ່ມ...
                      </span>
                    ) : hasNextPage ? (
                      <button
                        type="button"
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 font-semibold text-slate-600 shadow-sm"
                        onClick={() => fetchNextPage()}
                      >
                        ໂຫລດເພີ່ມ
                      </button>
                    ) : (
                      "ສະແດງລາຍການຄົບແລ້ວ"
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex h-[240px] items-center justify-center text-sm text-slate-400">
                  ບໍ່ມີຂໍ້ມູນ
                </div>
              )}
            </div>

            {selectedDonation && (
              <Modal
                show={!!selectedDonation}
                onHide={() => setSelectedDonation(null)}
                centered
                className="donation-detail-modal-backdrop"
                dialogClassName="donation-detail-modal-dialog"
              >
                <Modal.Header closeButton>
                  <Modal.Title>Donation Detail</Modal.Title>
                </Modal.Header>
                <Modal.Body className="donation-detail-modal-body max-h-[70vh] overflow-y-auto">
                  <div className="rounded-lg border border-[#dbe4ef] bg-[#f8fafc] p-3 dark:border-slate-700 dark:bg-slate-800">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                          {statusLabel[selectedDonation?.status || ""] ||
                            selectedDonation?.status ||
                            "--"}
                        </span>
                        <div className="mt-2 text-[12px] font-bold text-[#667085] dark:text-slate-400">
                          {formatDateTime(selectedDonation?.createdAt)}
                        </div>
                      </div>
                      <div className="flex-none text-right">
                        <div className="text-xl font-black leading-tight text-emerald-600">
                          +{formatCurrency(selectedDonation?.amount || 0)}
                        </div>
                        <div className="text-[11px] font-bold text-[#667085] dark:text-slate-400">
                          {selectedDonation?.currency || "ກີບ"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2">
                    <DonationDetailField
                      label="ID"
                      value={getDonationId(selectedDonation)}
                      mono
                    />
                    <DonationDetailField
                      label="Donor"
                      value={getDonorName(selectedDonation)}
                    />
                    <DonationDetailField
                      label="Message"
                      value={selectedDonation?.message?.trim() || "--"}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <DonationDetailField
                        label="Payment"
                        value={getPaymentMethodLabel(selectedDonation)}
                      />
                      <DonationDetailField
                        label="Shown"
                        value={`${formatCurrency(
                          getDisplayCount(selectedDonation),
                        )} ຄັ້ງ`}
                      />
                    </div>
                    <DonationDetailField
                      label="Last Displayed"
                      value={formatDateTime(selectedDonation?.lastDisplayedAt)}
                    />
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="outline-secondary"
                    onClick={() => handleCopyDonationId(selectedDonationId)}
                    disabled={!selectedDonationId}
                  >
                    Copy ID
                  </Button>
                  <Button
                    variant="primary"
                    disabled={
                      !selectedDonationId || showOnScreenMutation.isLoading
                    }
                    onClick={() =>
                      showOnScreenMutation.mutate(selectedDonationId)
                    }
                  >
                    {isSelectedDonationShowing ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        ກຳລັງສົ່ງ
                      </>
                    ) : (
                      <>
                        <RiPushpinFill />
                        ສົ່ງຂື້ນຈໍ
                      </>
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>
            )}

            <div className="hidden md:block table-responsive">
              <Table hover borderless className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="w-[20px]">
                      <Form.Check
                        type="checkbox"
                        checked={
                          selectedAccounts.length === donationIds.length &&
                          donationIds.length > 0
                        }
                        disabled={donationIds.length === 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th className="w-[20px]">No.</th>
                    <th className="text-start">ID</th>
                    <th>ຜູ້ບໍລິຈາກ</th>
                    <th>ຂໍ້ຄວາມ</th>
                    <th className="text-right">ຈຳນວນເງິນ</th>
                    <th>ສະຖານະ</th>
                    <th>ຊ່ອງທາງຊຳລະເງິນ</th>
                    <th>ວັນທີ</th>
                    <th className="text-center">ຈຳນວນຂື້ນຈໍ</th>
                    <th>ຂື້ນຈໍຫຼ້າສຸດ</th>
                    <th>ເຄື່ອງມື</th>
                  </tr>
                </thead>
                <tbody>
                  {isInitialListLoading ? (
                    <tr>
                      <td colSpan={12} className="text-center text-muted py-4">
                        ກຳລັງໂຫລດຂໍ້ມູນ...
                      </td>
                    </tr>
                  ) : donationList.length > 0 ? (
                    <>
                      {donationList.map((donation, index) => {
                        const donationId = getDonationId(donation);
                        const isShowing =
                          showOnScreenMutation.isLoading &&
                          showingDonationId === donationId;

                        return (
                          <tr key={donationId || index}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedAccounts.includes(donationId)}
                                onChange={(ev) =>
                                  handleSelectAccount(
                                    donationId,
                                    ev.target.checked,
                                  )
                                }
                                disabled={!donationId}
                              />
                            </td>
                            <td>{index + 1}</td>
                            <td>
                              <button
                                type="button"
                                className="border-0 bg-transparent text-muted text-truncate d-inline-block p-0"
                                style={{
                                  maxWidth: 180,
                                  cursor: donationId ? "copy" : "default",
                                }}
                                title={
                                  donationId ? "ກົດເພື່ອສຳເນົາ ID" : undefined
                                }
                                disabled={!donationId}
                                onClick={() => handleCopyDonationId(donationId)}
                              >
                                {getDonationIdPreview(donationId)}
                              </button>
                            </td>
                            <td>
                              <strong>{getDonorName(donation)}</strong>
                            </td>
                            <td>{donation?.message?.trim() || "--"}</td>
                            <td>
                              <span className="text-right d-block">
                                {formatCurrency(donation?.amount || 0)}{" "}
                                {donation?.currency || "ກີບ"}
                              </span>
                            </td>
                            <td>
                              {statusLabel[donation?.status || ""] ||
                                donation?.status ||
                                "--"}
                            </td>
                            <td>{getPaymentMethodLabel(donation)}</td>
                            <td>{formatDateTime(donation?.createdAt)}</td>
                            <td className="text-center">
                              {formatCurrency(getDisplayCount(donation))}
                            </td>
                            <td>{formatDateTime(donation?.lastDisplayedAt)}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={
                                  !donationId || showOnScreenMutation.isLoading
                                }
                                onClick={() =>
                                  showOnScreenMutation.mutate(donationId)
                                }
                              >
                                {isShowing ? (
                                  <>
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="mr-1"
                                    />
                                    ກຳລັງສົ່ງ
                                  </>
                                ) : (
                                  <>
                                    <RiPushpinFill className="mr-1" />
                                    ສົ່ງຂື້ນຈໍ
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {(isFetchingNextPage || hasNextPage) && (
                        <tr>
                          <td colSpan={12} className="text-center py-3">
                            {isFetchingNextPage ? (
                              <span className="text-muted">
                                ກຳລັງໂຫລດເພີ່ມ...
                              </span>
                            ) : (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => fetchNextPage()}
                              >
                                ໂຫລດເພີ່ມ
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center text-muted py-4">
                        ບໍ່ມີຂໍ້ມູນ
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </div>
    </main>
  );
}
